/**
 * VoucherPreviewCard — Phase 8.
 *
 * Renders the "Ready to convert N vouchers" panel that appears after a
 * daily sheet is saved. Loads a preview from the backend, splits into
 * auto-convertible vs Section 40A(3)-flagged rows, and offers a review
 * dialog for the flagged rows before atomic commit.
 *
 * Props:
 *   sheetId         : uuid of the saved daily_sheets row (required)
 *   onCommitted     : callback fired after successful commit
 *   alreadyGenerated: true when vouchers were already committed for this sheet
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    FileCheck,
    AlertTriangle,
    CircleCheck,
    Receipt,
    ArrowRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { api, fmtMoney } from '../lib/api';


export function VoucherPreviewCard({ sheetId, onCommitted, alreadyGenerated }) {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [decisions, setDecisions] = useState({});
    const [committing, setCommitting] = useState(false);

    useEffect(() => {
        if (!sheetId || alreadyGenerated) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const p = await api.previewVouchers(sheetId);
                if (!cancelled) setPlan(p);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to preview vouchers.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [sheetId, alreadyGenerated]);

    // Already-committed state — show a compact confirmation, nothing to do.
    if (alreadyGenerated) {
        return (
            <Card className="p-4 border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20" data-testid="vouchers-already-done">
                <div className="flex items-center gap-3">
                    <CircleCheck className="text-emerald-600 shrink-0" size={20} />
                    <div className="flex-1">
                        <div className="font-medium">Accounting vouchers generated</div>
                        <div className="text-xs text-muted-foreground">This sheet has already been converted. Check the Vouchers ledger to view or export.</div>
                    </div>
                </div>
            </Card>
        );
    }

    if (loading) return <Card className="p-4"><Skeleton className="h-16" /></Card>;
    if (!plan) return null;

    const { auto = [], flagged = [], summary = {} } = plan;
    const hasRows = auto.length + flagged.length > 0;
    if (!hasRows) return null;

    async function commitAll() {
        setCommitting(true);
        try {
            // Assemble decisions list from the dialog state + defaults.
            const decisionList = [
                ...auto.map((v) => ({ row_id: v.row_id, action: 'save' })),
                ...flagged.map((v) => {
                    const d = decisions[v.row_id] || {};
                    return {
                        row_id: v.row_id,
                        action: d.action || 'save',
                        override_mode: d.override_mode || undefined,
                        compliance_note: d.compliance_note || undefined,
                    };
                }),
            ];
            const res = await api.commitVouchers(sheetId, decisionList);
            toast.success(
                `${res.created} voucher(s) created · ${res.stock_entries} stock entries`
                + (res.section_40a3_disallowed_total > 0
                    ? ` · ⚠ ₹${res.section_40a3_disallowed_total} disallowed (40A(3))`
                    : ''),
            );
            setDialogOpen(false);
            onCommitted?.(res);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === 'object' && detail?.error) {
                toast.error(`${detail.error} (${detail.count || 0} vouchers)`);
            } else {
                toast.error(detail || 'Commit failed.');
            }
        } finally {
            setCommitting(false);
        }
    }

    const needsReview = flagged.length > 0;

    return (
        <>
            <Card className="p-4 border-primary/40 bg-primary/5" data-testid="voucher-preview-card">
                <div className="flex items-start gap-3">
                    <FileCheck className="text-primary shrink-0 mt-0.5" size={20} />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                            Ready to convert {summary.total_rows || 0} expense row(s) into accounting vouchers
                            <Badge variant="outline" className="text-[10px]">₹{fmtMoney(summary.total_amount || 0)}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                            <span className="flex items-center gap-1">
                                <CircleCheck size={12} className="text-emerald-600" />
                                {auto.length} auto
                            </span>
                            {needsReview && (
                                <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                                    <AlertTriangle size={12} />
                                    {flagged.length} need review (Section 40A(3))
                                </span>
                            )}
                        </div>
                    </div>
                    <Button
                        onClick={() => {
                            if (needsReview) setDialogOpen(true);
                            else commitAll();
                        }}
                        disabled={committing}
                        className="gap-1"
                        data-testid="voucher-preview-primary-btn"
                    >
                        {needsReview ? (
                            <>Review {flagged.length} <ArrowRight size={14} /></>
                        ) : (
                            <><Receipt size={14} /> {committing ? 'Converting…' : 'Convert all'}</>
                        )}
                    </Button>
                </div>
            </Card>

            {needsReview && (
                <Dialog open={dialogOpen} onOpenChange={(v) => { if (!committing) setDialogOpen(v); }}>
                    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="s40a3-review-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="text-amber-600" size={18} />
                                Section 40A(3) — Cash payment review
                            </DialogTitle>
                            <DialogDescription>
                                Cash payments over ₹10,000 to the same category on the same day cannot be claimed as business expense (Section 40A(3), Income Tax Act). Choose how to record each:
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-3">
                            {flagged.map((v) => {
                                const d = decisions[v.row_id] || { action: 'save' };
                                return (
                                    <Card key={v.row_id} className="p-3 border-amber-300 bg-amber-50/40 dark:bg-amber-950/20" data-testid={`s40a3-row-${v.row_id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <div className="font-medium">{v.party}</div>
                                                <div className="text-xs text-muted-foreground">{v.flag_detail}</div>
                                            </div>
                                            <div className="text-right font-mono tabular-nums font-semibold">
                                                ₹{fmtMoney(v.amount)}
                                            </div>
                                        </div>
                                        <Select
                                            value={d.action || 'save'}
                                            onValueChange={(action) => {
                                                setDecisions((p) => ({
                                                    ...p,
                                                    [v.row_id]: {
                                                        ...(p[v.row_id] || {}),
                                                        action,
                                                        override_mode: action === 'convert_bank' ? (p[v.row_id]?.override_mode || 'UPI') : undefined,
                                                    },
                                                }));
                                            }}
                                        >
                                            <SelectTrigger className="h-9 text-sm" data-testid={`s40a3-action-${v.row_id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="convert_bank">Paid via UPI / Bank — convert voucher</SelectItem>
                                                <SelectItem value="save">Paid in cash — save with disallowed tag</SelectItem>
                                                <SelectItem value="skip">Skip — do not create voucher</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {d.action === 'convert_bank' && (
                                            <div className="mt-2">
                                                <Select
                                                    value={d.override_mode || 'UPI'}
                                                    onValueChange={(m) => setDecisions((p) => ({
                                                        ...p, [v.row_id]: { ...(p[v.row_id] || {}), override_mode: m },
                                                    }))}
                                                >
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`s40a3-mode-${v.row_id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="UPI">UPI</SelectItem>
                                                        <SelectItem value="BANK">Bank Transfer</SelectItem>
                                                        <SelectItem value="NEFT">NEFT</SelectItem>
                                                        <SelectItem value="CHEQUE">Cheque</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={committing} data-testid="s40a3-cancel">
                                Cancel
                            </Button>
                            <Button onClick={commitAll} disabled={committing} className="gap-1" data-testid="s40a3-confirm">
                                <Receipt size={14} /> {committing ? 'Converting…' : `Convert all ${summary.total_rows} vouchers`}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

export default VoucherPreviewCard;
